import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Switch,
  Typography,
} from '@mui/material';
import {
  ChipsInput,
  InputGroup,
  SelectInput,
  useEditContext,
} from '@prismamedia/one-crud-react';
import JSON5 from 'json5';
import { ResourceType } from 'pages/CampaignPage/CampaignEditPage/ContentPage/utils/useGetBlockItems';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { BasicContainer } from '../../../../../components/BasicContainer';
import { auth } from '../../../../../utils/auth';
import { getTimeOptions } from '../ContentFields/utils';
import { useStyles } from '../styles';
import { AutomationConfig } from './AutomationConfig';
import {
  DAYS_FREQUENCY_OPTIONS,
  INITIAL_CONFIG_LIST,
  JSON_STRINGIFY_OPTS,
  MAIN_TITLE,
} from './constants';
import { getWeekDaysSuggestions } from './utils';

export interface AutomationConfigType {
  type?: ResourceType;
  articleCount?: number;
  categoryIds?: string[];
  dayRange?: number;
  sourcesIds?: string[];
  tagIds?: string[];
  withoutAnalytics?: boolean;
}
interface UpdateAutomationParams {
  index: number;
  newAutomationTime?: number | null;
  newConfig?: AutomationConfigType;
  newQuery?: string | null;
}

interface NewsletterSection {
  sectionResourceCount: number;
}

interface AutomationFieldsProps {
  campaignSectionCount: string;
}

export const AutomationFields: FC<AutomationFieldsProps> = ({
  campaignSectionCount,
}) => {
  const firstRender = useRef(true);
  const isAdmin = useMemo(() => auth.user?.isSuperAdmin(), []);
  const classes = useStyles({ withoutChips: isAdmin });
  const context = useEditContext();
  const maxListLength = Number(campaignSectionCount);

  const [
    availableWeekDaysSuggestions,
    setAvailableWeekDaysSuggestions,
  ] = useState(getWeekDaysSuggestions(context.item.availableWeekDays));

  const [isAutomationActive, setIsAutomationActive] = useState(
    !!context.item.automationTime,
  );
  const [isAutovalidate, setIsAutovalidate] = useState<boolean>(
    !!context.item.autoValidateXMLCampaign,
  );
  const [configList, setConfigList] = useState<AutomationConfigType[]>(
    INITIAL_CONFIG_LIST,
  );

  const updateAutomation = ({
    index,
    newAutomationTime,
    newConfig,
  }: UpdateAutomationParams) => {
    setConfigList((currentConfigList) => {
      const newConfigList = [...currentConfigList];
      // eslint-disable-next-line immutable/no-mutation
      newConfigList[index] =
        newConfig || newConfigList[index] || INITIAL_CONFIG_LIST[0];

      if (newConfigList.length > maxListLength) {
        // eslint-disable-next-line fp/no-mutating-methods
        newConfigList.splice(maxListLength, newConfigList.length);
      }

      if (!newConfigList[index].type) {
        // eslint-disable-next-line immutable/no-mutation
        newConfigList[index].type = ResourceType.EMPTY;
      }

      const newContextConfig = isAutomationActive
        ? JSON.stringify(newConfigList.map((config) => config))
        : null;

      // eslint-disable-next-line immutable/no-mutation
      context.item = {
        ...context.item,
        automationConfig: newContextConfig,
        automationQuery: newContextConfig,
        automationTime: newAutomationTime,
        ...(!isAutomationActive && {
          available: null,
          availableWeekDays: [],
        }),
      };

      return newConfigList;
    });
  };

  const handleAutomationConfigChange = (index: number) => (
    field: keyof AutomationConfigType,
  ) => (value?: string | string[] | number | boolean) => {
    const newConfig = { ...configList[index], [field]: value };
    const newQuery = JSON5.stringify(newConfig, JSON_STRINGIFY_OPTS) || '';
    const newAutomationTime = context?.item.automationTime;
    updateAutomation({ index, newAutomationTime, newConfig, newQuery });
  };

  const handleAutomationActive = (checked: boolean) => {
    setIsAutomationActive(checked);

    if (isAutovalidate && !checked) {
      handleAutovalidationActive(checked);
    }
  };

  const handleAutovalidationActive = (checked: boolean) => {
    setIsAutovalidate(checked);
    // eslint-disable-next-line immutable/no-mutation
    context.item.autoValidateXMLCampaign = checked;
  };

  const buildAutomationConfig = () => {
    let automationConfig: AutomationConfigType[] = context.item
      ?.automationConfig
      ? JSON5.parse(context.item?.automationConfig)
      : [];

    if (!automationConfig.length && context.item.sections) {
      automationConfig = (JSON.parse(
        context.item.sections,
      ) as NewsletterSection[]).map((section) => ({
        articleCount: section.sectionResourceCount,
      }));
    }

    return automationConfig;
  };

  useEffect(() => {
    /**
     * @description Explaination :
     * 1) For the first render, both newConfig and newQuery values comes from the context (parsedConfig)
     * 2) For all other cases, these variables has their own default value
     * To preserve already exiting configs on the view, the function "updateAutomation" replace the default values by the current ones if they exists
     * @Todo This hook can be split in two to be more accurate/readable
     */
    const parsedConfig = isAutomationActive ? buildAutomationConfig() : [];

    // eslint-disable-next-line fp/no-loops
    for (let index = 0; index < maxListLength; index++) {
      const newAutomationTime = isAutomationActive
        ? context?.item.automationTime
        : null;

      const newConfig = (isAutomationActive && parsedConfig?.[index]) || {};

      const newQuery = isAutomationActive
        ? JSON5.stringify(newConfig, JSON_STRINGIFY_OPTS)
        : '';
      updateAutomation({
        index,
        newAutomationTime,
        newConfig,
        newQuery,
      });
    }

    // eslint-disable-next-line immutable/no-mutation
    firstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxListLength, isAutomationActive]);

  return (
    <BasicContainer title="Automatisation">
      <div className={classes.switchContainer}>
        <Typography>{MAIN_TITLE}</Typography>
        <Switch
          checked={isAutomationActive}
          color="primary"
          data-testid="automation-switch"
          onChange={(event) => {
            handleAutomationActive(event.target.checked);
          }}
        />
      </div>
      {isAutomationActive && (
        <div className={classes.switchContainer}>
          <Typography>Autovalidation de la newsletter</Typography>
          <Switch
            checked={isAutovalidate}
            color="primary"
            data-testid="autovalidate-switch"
            onChange={(event) => {
              handleAutovalidationActive(event.target.checked);
            }}
          />
        </div>
      )}
      {isAutomationActive && (
        <div className={classes.wrapperAutomationFields}>
          <InputGroup>
            <SelectInput
              field="frequency"
              label="Fréquence *"
              options={DAYS_FREQUENCY_OPTIONS}
            />
          </InputGroup>

          <InputGroup variant="vertical" className={classes.weekDays}>
            <ChipsInput
              field="availableWeekDays"
              item={context.item}
              autoSuggestFieldProps={{
                fetchSuggestions: () =>
                  new Promise((resolve) => {
                    resolve(availableWeekDaysSuggestions);
                  }),
                keepSelectedValue: false,
                suggestionStyle: classes.suggestionStyle,
              }}
              onChange={(_, value) => {
                // eslint-disable-next-line immutable/no-mutation
                context.item.availableWeekDays = value;
                setAvailableWeekDaysSuggestions(getWeekDaysSuggestions(value));
              }}
            />
          </InputGroup>

          <InputGroup className={classes.spacingTop}>
            <SelectInput
              field="automationTime"
              label="Heure de création de la campagne *"
              options={getTimeOptions()}
            />
          </InputGroup>

          <div className={classes.wrapperAccordions}>
            {configList.map((_, i) => (
              <Accordion key={i}>
                <AccordionSummary
                  aria-controls={`section-${i}-content`}
                  expandIcon={<ExpandMoreIcon />}
                  id={`section-${i}-header`}
                >
                  <>
                    <Typography className={classes.accordionHeading}>
                      Section {i + 1}
                    </Typography>
                  </>
                </AccordionSummary>
                <AccordionDetails classes={{ root: classes.accordionDetails }}>
                  <AutomationConfig
                    isFromNlf={context.item.isFromNlf}
                    brandKey={context.item.brandKey}
                    config={configList[i]}
                    onConfigChange={handleAutomationConfigChange(i)}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        </div>
      )}
    </BasicContainer>
  );
};
